import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/domain/constants/routes";
import { contextService } from "@/domain/services/contextService";
import { usePageService } from "@/application/services/pageService";

/**
 * 對話式建站頁面
 * 顯示與 AI 助手的對話介面
 */
export function AccountInitPage() {
  const navigate = useNavigate();
  const [_, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const steps = [
    {
      title: "正在驗證您的帳號",
      message: "確認您的身份資訊並建立安全連線",
      duration: 2000,
    },
    {
      title: "正在建立工作空間",
      message: "為您配置個人化的 AI 助手環境",
      duration: 2500,
    },
    {
      title: "正在啟動 AI 助手",
      message: "載入專業助手模組，準備為您服務",
      duration: 2000,
    },
    {
      title: "即將完成設定",
      message: "最後檢查，馬上就可以開始使用了",
      duration: 1500,
    },
  ];

  useEffect(() => {
    const t = setInterval(async () => {
      const context = await contextService.getCurrentContext();
      if (context.current_project_id && context.current_page_id) {
        const page = await usePageService.getPageById(
          context.current_project_id,
          context.current_page_id,
        );
        if (page && page.page_conversation_id) {
          clearInterval(t);
          navigate(ROUTES.CONVERSATION, { replace: true });
        }
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const progressElement = document.getElementById("progress-percentage");
    const titleElement = document.getElementById("status-title");
    const messageElement = document.getElementById("status-message");

    // Smooth progress increment
    const targetProgress = (currentStep + 1) * 25;
    const t = setTimeout(() => {
      if (currentProgress < targetProgress) {
        setCurrentProgress(currentProgress + 1);
        if (progressElement)
          progressElement.textContent = currentProgress + "%";
      } else {
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }
    }, 40);
    setTimer(t);
    // Update status text
    if (titleElement) titleElement.textContent = steps[currentStep].title;
    if (messageElement) messageElement.textContent = steps[currentStep].message;

    // Update step visual state
    for (let i = 0; i <= currentStep; i++) {
      const stepElement = document.getElementById(`step-${i + 1}`);
      if (!stepElement) continue;
      if (i < currentStep) {
        // Completed step
        stepElement.classList.remove("active");
        stepElement.classList.add("completed");

        const icon = stepElement.querySelector(".step-icon i");
        if (icon) icon.className = "fas fa-check text-sm text-white";
      } else if (i === currentStep) {
        // Current active step
        stepElement.classList.add("active");

        const icon = stepElement.querySelector(".step-icon i");
        if (!icon) continue;
        if (currentStep === 0)
          icon.className = "fas fa-user-check text-sm text-white";
        else if (currentStep === 1)
          icon.className = "fas fa-cogs text-sm text-white";
        else if (currentStep === 2)
          icon.className = "fas fa-robot text-sm text-white";
        else if (currentStep === 3)
          icon.className = "fas fa-check text-sm text-white";
      }
    }
    // updateStepVisuals();
  }, [currentProgress, currentStep]);

  return (
    <>
      <main class="min-h-screen bg-cream local-pattern flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          <div class="loading-card rounded-2xl border border-tea-brown/40 overflow-hidden animate-fade-in-up">
            <div class="bg-gradient-to-r from-earth-green to-soft-green p-6 text-white text-center relative overflow-hidden">
              <div class="absolute inset-0 shimmer-bg opacity-20"></div>

              <div class="relative z-10">
                <div class="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-float">
                  <img
                    src="/assets/images/logo.jpg"
                    alt="麻薏 AI Logo"
                    class="w-12 h-12 rounded-lg"
                  />
                </div>

                <h1 class="text-2xl md:text-3xl font-bold handwriting mb-2">
                  歡迎加入麻薏 AI！
                </h1>
                {/* <p class="text-white/90 text-md md:text-base">
                  正在為您準備專屬的工作環境
                </p> */}
              </div>
            </div>

            <div class="p-6">
              <div class="relative flex items-center justify-center mb-8">
                <svg class="w-24 h-24 progress-ring" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#F5F1E8"
                    stroke-width="8"
                    fill="none"
                  ></circle>
                  <circle
                    class="progress-ring-circle"
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#6B8E5A"
                    stroke-width="8"
                    fill="none"
                    stroke-linecap="round"
                  ></circle>
                </svg>

                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="text-center">
                    <div
                      id="progress-percentage"
                      class="text-2xl font-bold text-earth-green handwriting"
                    >
                      0%
                    </div>
                    <div class="text-xs text-tea-brown">設定中</div>
                  </div>
                </div>
              </div>

              <div class="text-center mb-4">
                <h2
                  id="status-title"
                  class="text-lg font-semibold text-earth-green mb-2 handwriting"
                >
                  正在初始化您的帳號
                </h2>
                <p
                  id="status-message"
                  class="text-tea-brown text-sm leading-relaxed"
                >
                  這是您第一次使用麻薏 AI，我們正在為您設定專屬環境
                  <span class="dots-loader ml-1">
                    <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                    <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                    <span class="w-1 h-1 bg-earth-green rounded-full inline-block"></span>
                  </span>
                </p>
              </div>

              <div class="space-y-4 mb-8">
                <div id="step-1" class="step-item flex items-center space-x-3">
                  <div class="step-icon w-8 h-8 bg-warm-beige rounded-full flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-user-check text-sm text-tea-brown"></i>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-medium text-tea-brown">
                      驗證您的帳號身份
                    </div>
                    <div class="text-xs text-clay-brown/70">確保帳號安全性</div>
                  </div>
                </div>

                <div id="step-2" class="step-item flex items-center space-x-3">
                  <div class="step-icon w-8 h-8 bg-warm-beige rounded-full flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-cogs text-sm text-tea-brown"></i>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-medium text-tea-brown">
                      建立您的工作空間
                    </div>
                    <div class="text-xs text-clay-brown/70">配置個人化設定</div>
                  </div>
                </div>

                <div id="step-3" class="step-item flex items-center space-x-3">
                  <div class="step-icon w-8 h-8 bg-warm-beige rounded-full flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-robot text-sm text-tea-brown"></i>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-medium text-tea-brown">
                      啟動 AI 助手服務
                    </div>
                    <div class="text-xs text-clay-brown/70">
                      載入 12 種專業助手
                    </div>
                  </div>
                </div>

                <div id="step-4" class="step-item flex items-center space-x-3">
                  <div class="step-icon w-8 h-8 bg-warm-beige rounded-full flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-check text-sm text-tea-brown"></i>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-medium text-tea-brown">
                      完成設定並進入平台
                    </div>
                    <div class="text-xs text-clay-brown/70">
                      開始您的智慧營運之旅
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-warm-beige rounded-xl p-4">
                <div class="flex items-start space-x-3">
                  <div class="w-8 h-8 bg-earth-green/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i class="fas fa-info-circle text-earth-green text-sm"></i>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-md font-medium text-earth-green mb-1">
                      初次設定須知
                    </h3>
                    <ul class="text-md text-tea-brown space-y-1 leading-relaxed">
                      <li>• 初次登入可能需要 1-2 分鐘時間</li>
                      <li>• 我們會為您創建專屬的 AI 工作環境</li>
                      <li>• 所有個人資料都經過加密保護</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
